/**
 * Word-specific slash-command behaviour extras.
 *
 * These are merged into the backend-supplied slash-command list by the
 * registry at load time. Only commands that need frontend UX rules
 * (auto-send predicates, bubble echo) appear here — commands that just
 * prefill the input need no entry.
 *
 * Keys must match the ``slash`` field returned by the backend
 * (``GET /api/v1/skills/slash-commands/word``) including the leading "/".
 */
import type { SlashCommandExtras } from "../../../chat/slashCommands/types";

export const WORD_SLASH_EXTRAS: Readonly<Record<string, SlashCommandExtras>> = {
  /**
   * /rewrite — auto-sends when the user has text selected in the document
   * so the AI immediately rewrites the selection without extra prompting.
   */
  "/rewrite": {
    autoSendWhen: (ctx) => ctx.hasSelectedText,
    autoSendMessage: () => "Rewrite the selected text.",
  },

  /**
   * /review — auto-sends when text is selected; returns tracked-change
   * suggestions for the selection.
   */
  "/review": {
    autoSendWhen: (ctx) => ctx.hasSelectedText,
    autoSendMessage: () => "Review and suggest improvements for the selected text.",
  },

  /**
   * /start-tracking — always auto-sends: turning on Word's Track Changes takes
   * no arguments. It no longer asks for a change report (AIVA-8040): nothing has
   * been tracked at the moment tracking starts, so the report belongs on
   * /stop-tracking.
   */
  "/start-tracking": {
    autoSendWhen: () => true,
    autoSendMessage: () => "Turn on Track Changes for this document.",
    // The bubble shows "/start-tracking", not the menu description. Picking an
    // always-auto-send slash out of the palette submits `cmd.prompt`, which the
    // backend fills from `slashDescription` — so the transcript read "Turn on
    // Track Changes — mark every edit as a revision" as if the user had typed
    // the blurb. There are no args to fold in here, so the slash is both the
    // shortest and the truest record of what was run.
    echoSlashInBubble: true,
  },

  /**
   * /toc — always auto-sends: the TOC is built from the document's own
   * heading structure, so there are no args for the user to add.
   */
  "/toc": {
    autoSendWhen: () => true,
    autoSendMessage: () => "Generate a table of contents for this document.",
  },

  /**
   * /stop-tracking — always auto-sends: it turns off Track Changes and reports
   * what changed in the document this session (the session change log is
   * attached to the turn by buildDocumentContext), so there are no args for the
   * user to add. The report moved here from /start-tracking in AIVA-8040.
   */
  "/stop-tracking": {
    autoSendWhen: () => true,
    autoSendMessage: () => "Turn off Track Changes and show what has changed in this document this session.",
    // Same as /start-tracking above — no args, so echo the slash rather than
    // the "Turn off Track Changes and show what changed this session" blurb.
    echoSlashInBubble: true,
  },
};
